import React, {useEffect, useMemo} from 'react';
import {
	FlexRow,
	FlexItem,
	TextField,
	Label,
	FlexColumn,
	DropdownList,
	ButtonDropdownField,
	DateRangePicker,
	Text,
} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {HIDDEN_FEATURES} from '../../../constants';
import ColorsPicker, {ColorsPickerDesignSystemSizeWrapper} from '../../../components/colors_picker';
import {isFeatureHidden} from '../../../forecast-app/shared/util/CompanySetupUtil';

export type SageLocationEntityOption = {
	locationId: string;
	name: string;
	isPrimaryConnection: boolean;
};

type ProjectMainInfoProps = {
	label: string;
	projectName: string;
	onProjectNameChange: (value: string) => void;
	onProjectNameBlur: () => void;
	invalidProjectName: boolean;
	projectNameErrorMessage: string;
	customProjectId: string;
	defaultCustomProjectId: string;
	onCustomProjectIdChange: (value: string) => void;
	onCustomProjectIdBlur: (value: string) => void;
	customProjectIdError: string;
	projectColor: string;
	onColorSelect: (color: string) => void;
	startDate: Date;
	endDate: Date;
	handleDateRangeChange: (startDate: Date, endDate: Date) => void;
	client: string;
	clientOptions: {label: string; value: string; cy: string}[];
	onClientChange: (client: {label: string; value: string}) => void;
	locationEntity: string | null;
	locationEntityOptions: SageLocationEntityOption[];
	onLocationEntityChange: (option: SageLocationEntityOption) => void;
	locationEntityError?: string;
	useDuplication: boolean;
	duplicateColor: boolean;
	isOldCompany: boolean;
	isProjectLocked: boolean;
	lastCompanyProjectId: number;
	isSageIntacctEnabled: boolean;
};

const NO_SAGE_ENTITY_NAME = 'No entity name';

function getSageEntityOptionLabel(option: SageLocationEntityOption): string {
	return option.name?.trim() ? option.name : NO_SAGE_ENTITY_NAME;
}

function getLocationEntityTriggerLabel(locationEntity: string | null, options: SageLocationEntityOption[]): string {
	if (locationEntity == null) {
		return '';
	}
	const selected = options.find(o => o.locationId === locationEntity);
	return selected != null ? getSageEntityOptionLabel(selected) : '';
}

export function ProjectMainInfo(props: ProjectMainInfoProps): JSX.Element {
	const {formatMessage} = useIntl();

	const {isSageIntacctEnabled, locationEntityOptions} = props;

	const primarySageLocationEntity = locationEntityOptions.find(o => o.isPrimaryConnection);
	const isPrimaryLocationEntityTopEntity = primarySageLocationEntity?.locationId === 'NONE';

	const disabledClientDropdown = useMemo(() => {
		return isSageIntacctEnabled && isPrimaryLocationEntityTopEntity && props.locationEntity === null;
	}, [isSageIntacctEnabled, isPrimaryLocationEntityTopEntity, props.locationEntity]);

	useEffect(() => {
		if (isSageIntacctEnabled && !isPrimaryLocationEntityTopEntity && primarySageLocationEntity) {
			props.onLocationEntityChange(primarySageLocationEntity);
		}
	}, []);

	return (
		<FlexColumn gap={'l'}>
			<FlexRow alignItems={'start'}>
				<TextField
					data-cy={'new-project-name'}
					label={props.label}
					onValueChange={props.onProjectNameChange}
					onBlur={props.onProjectNameBlur}
					value={props.projectName}
					placeholder={formatMessage({id: 'new_project_modal.project_name_placeholder'})}
					errorText={props.projectNameErrorMessage}
					maxLength={191}
				/>
			</FlexRow>
			<FlexRow>
				{/* This fixed value 350px is to match the date input below. It is not half of the modal, but aligns well the Sage Entity input. */}
				<FlexItem flex={'0 1 350px'}>
					<FlexColumn gap={'xs'}>
						<FlexRow>
							<FlexItem>
								<TextField
									data-cy={'new-project-customId'}
									label={formatMessage({id: 'new_project_modal.project_id'})}
									onValueChange={props.onCustomProjectIdChange}
									onBlur={props.onCustomProjectIdBlur}
									hasError={!!props.customProjectIdError}
									value={props.customProjectId}
									maxLength={6}
									placeholder={`P${props.lastCompanyProjectId + 1}`}
								/>
							</FlexItem>
							{props.useDuplication && props.duplicateColor ? null : (
								<FlexColumn>
									<Label>{formatMessage({id: 'common.color'})}</Label>
									<ColorsPickerDesignSystemSizeWrapper>
										<ColorsPicker
											value={props.projectColor}
											rectangle={true}
											onColorSelect={props.onColorSelect}
											projectLocked={undefined}
											usePortal={true}
											cy={'new-project-color'}
										/>
									</ColorsPickerDesignSystemSizeWrapper>
								</FlexColumn>
							)}
						</FlexRow>
						{props.customProjectIdError ? (
							<FlexRow>
								<Text color="error" noWrap data-cy={'new-project-customId-error'}>
									{props.customProjectIdError}
								</Text>
							</FlexRow>
						) : null}
					</FlexColumn>
				</FlexItem>
				{props.isSageIntacctEnabled ? (
					<FlexColumn>
						<Label>{formatMessage({id: 'new_project_modal.sage_intacct_entity_input_label'})}</Label>
						<DropdownList>
							<DropdownList.Trigger data-cy={'new-project-location-entity'}>
								<ButtonDropdownField
									hasError={!!props.locationEntityError}
									errorText={props.locationEntityError}
									data-cy={'new-project-location-entity-field'}
								>
									<Text>
										{getLocationEntityTriggerLabel(props.locationEntity, props.locationEntityOptions)}
									</Text>
								</ButtonDropdownField>
							</DropdownList.Trigger>
							<DropdownList.Content align={'start'}>
								{props.locationEntityOptions.map(opt => (
									<DropdownList.Item
										key={opt.locationId}
										data-cy={`new-project-location-entity-${opt.locationId}`}
										onClick={() => props.onLocationEntityChange(opt)}
									>
										<FlexRow>
											<Text>{getSageEntityOptionLabel(opt)}</Text>
										</FlexRow>
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						</DropdownList>
					</FlexColumn>
				) : null}
			</FlexRow>
			<FlexRow>
				<FlexItem>
					<DateRangePicker
						data-cy={'new-project-dates'}
						label={formatMessage({id: 'common.dates'})}
						disabled={props.isProjectLocked}
						startDate={props.startDate}
						endDate={props.endDate}
						onChange={props.handleDateRangeChange}
					/>
				</FlexItem>

				{!isFeatureHidden(HIDDEN_FEATURES.CLIENT) ? (
					<FlexColumn>
						<Label>{formatMessage({id: 'common.client'})}</Label>
						<DropdownList>
							<DropdownList.Trigger data-cy={'settings-project-stage'}>
								<ButtonDropdownField disabled={disabledClientDropdown}>
									<Text>{props.clientOptions.find(o => o.value === props.client)?.label || 'No Client'}</Text>
								</ButtonDropdownField>
							</DropdownList.Trigger>

							<DropdownList.Content align={'start'}>
								{props.clientOptions.map(client => (
									<DropdownList.Item
										key={client.value}
										data-cy={client.cy}
										onClick={() => props.onClientChange(client)}
									>
										<FlexRow>
											<Text>{client.label}</Text>
										</FlexRow>
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						</DropdownList>
					</FlexColumn>
				) : null}
			</FlexRow>
		</FlexColumn>
	);
}
